import React, { useState } from 'react';
import { useStore } from '@/context/StoreContext';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';

export const QuoteDialog = ({ trigger, product = null, defaultType = 'rare_search', source = 'site' }) => {
  const { t, lang, customer } = useStore();
  const [open, setOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: customer?.full_name || '', email: customer?.email || '', phone: '', company: '', inquiry_type: defaultType,
    quantity: 1, delivery_country: '', message: '',
  });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.createQuote({
        ...form, quantity: Number(form.quantity) || 1,
        product_id: product?.id, product_name: product?.name,
        source: product ? 'product_page' : source, lang,
      });
      setSent(true);
      toast.success(t.quote.success);
      setTimeout(() => { setOpen(false); setSent(false); }, 2000);
    } catch (err) { toast.error(t.common.error); }
    finally { setLoading(false); }
  };

  const field = 'w-full bg-transparent border border-nw-gold/25 rounded-[3px] px-3 h-11 text-nw-ivory placeholder:text-nw-warmgrey/60 focus:outline-none focus:border-nw-gold text-sm';
  const label = 'block text-[10px] tracking-luxe uppercase text-nw-warmgrey mb-2';

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSent(false); if (v && customer) setForm((f) => ({ ...f, name: f.name || customer.full_name, email: f.email || customer.email })); }}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-nw-espresso border-nw-gold/25 text-nw-ivory max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-nw-gold tracking-wide">{t.quote.title}</DialogTitle>
          <DialogDescription className="text-sm text-nw-warmgrey leading-relaxed">{t.quote.subtitle}</DialogDescription>
        </DialogHeader>
        {sent ? (
          <div data-testid="quote-success" className="py-8 text-center">
            <p className="text-nw-ivory">{t.quote.success}</p>
            <button className="nw-btn-gold mt-6" onClick={() => setOpen(false)}>OK</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            {product && (
              <div className="text-sm border border-nw-gold/20 rounded-[3px] px-3 py-2 text-nw-gold">{product.name}</div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div><label className={label}>{t.quote.name}</label><input required data-testid="quote-name-input" className={field} value={form.name} onChange={set('name')} /></div>
              <div><label className={label}>{t.quote.email}</label><input required type="email" data-testid="quote-email-input" className={field} value={form.email} onChange={set('email')} /></div>
              <div><label className={label}>{t.quote.phone}</label><input className={field} value={form.phone} onChange={set('phone')} /></div>
              <div><label className={label}>{t.quote.company}</label><input className={field} value={form.company} onChange={set('company')} /></div>
              <div>
                <label className={label}>{t.quote.type}</label>
                <Select value={form.inquiry_type} onValueChange={set('inquiry_type')}>
                  <SelectTrigger data-testid="quote-type-select" className={field}><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-nw-espresso border-nw-gold/25 text-nw-ivory">
                    {Object.entries(t.quote.types).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div><label className={label}>{t.quote.deliveryCountry}</label><input className={field} value={form.delivery_country} onChange={set('delivery_country')} /></div>
            </div>
            <div>
              <label className={label}>{t.quote.message}</label>
              <textarea data-testid="quote-form-message-textarea" rows={4} className={field + ' h-auto py-2'} value={form.message} onChange={set('message')} />
            </div>
            <button type="submit" disabled={loading} data-testid="quote-form-submit-button" className="nw-btn-gold w-full">{loading ? <Loader2 size={16} className="animate-spin" /> : t.quote.submit}</button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
